import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GET, POST } from 'rest/request'
import * as urls from 'rest/urls'
import { RootState } from 'store'

const initialState: warehouse.initialState = {
  status: 'idle',
  warehouses: [],
  state: {
    warehouses: {},
    places: {},
  }
}

export const getWarehouses = createAsyncThunk<warehouse.WarehousesResponse, string>('warehouses/getWarehouses', async (userId) => {
  return await GET(urls.warehouses(userId))
})

export const getObjectsState = createAsyncThunk<warehouse.ObjectState>('warehouses/getObjectsState', async () => {
  return POST(urls.getobjectstates)
})

export const warehousesSlice = createSlice({
  name: 'warehouses',
  initialState,
  reducers: {
    updateWarehouse: (state, action: PayloadAction<{ id: string, values: Partial<warehouse.Warehouse> }>) => {
      const { id, values } = action.payload
      if (id && values) {
        state.warehouses = state.warehouses.map(warehouse => warehouse.objectId === id
          ? { ...warehouse, ...values }
          : warehouse)
      }
    },
    updateWarehouseState: (state, action: PayloadAction<{ id: string, doorState: Partial<warehouse.ObjectStateWarehouse> }>) => {
      state.warehouses = state.warehouses.map(warehouse => warehouse.objectId === action.payload.id
        ? { ...warehouse, doorState: { ...warehouse.doorState, ...action.payload.doorState } }
        : warehouse)
      state.state = {
        ...(state.state || { gates: {}, gateways: {}, warehouses: {}, places: {} }),
        warehouses: {
          ...(state.state?.warehouses || {}),
          [action.payload.id]: {
            ...(state.state?.warehouses[action.payload.id] || { lock: 'locked', door: 'closed' }),
            ...action.payload.doorState
          }
        }
      }
    },
    updateGateState: (state, action: PayloadAction<{ id: string, placeId: string, state: warehouse.GateState }>) => {
      state.state = {
        ...(state.state || { warehouses: {}, places: {} }),
        places: {
          ...(state.state?.places || {}),
          [action.payload.placeId]: {
            ...(state.state?.places?.[action.payload.placeId] || { gates: {}, gateways: {} }),
            gates: {
              ...(state.state?.places?.[action.payload.placeId]?.gates || {}),
              [action.payload.id]: action.payload.state
            }
          }
        }
      }
    },
    updateGatewayState: (state, action: PayloadAction<{ id: string, placeId: string, state: warehouse.GatewayState }>) => {
      state.state = {
        ...(state.state || { warehouses: {}, places: {} }),
        places: {
          ...(state.state?.places || {}),
          [action.payload.placeId]: {
            ...(state.state?.places?.[action.payload.placeId] || { gates: {}, gateways: {} }),
            gateways: {
              ...(state.state?.places?.[action.payload.placeId]?.gateways || {}),
              [action.payload.id]: action.payload.state
            }
          }
        }
      }
    },
    clearWarehousesList: () => {
      return initialState
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getWarehouses.pending, (state) => {
        state.status = 'pending'
      })
      .addCase(getWarehouses.rejected, (state) => {
        state.status = 'failed'
      })
      .addCase(getWarehouses.fulfilled, (state, action) => {
        if (action.payload.data) {
          state.status = 'succeed'
          state.warehouses = action.payload.data.warehouses
        }
      })
      .addCase(getObjectsState.fulfilled, (state, action) => {
        if (action.payload) {
          state.state = action.payload
        }
      })
  },
})

export const { updateWarehouse, updateWarehouseState, updateGateState, updateGatewayState, clearWarehousesList } = warehousesSlice.actions

export const selectWarehouseStatus = (state: RootState) => state.warehouses.status
export const selectWarehouses = (state: RootState) => state.warehouses.warehouses
export const selectWarehouseIndex = (state: RootState, id: string) => {
  return state.warehouses.warehouses.findIndex(w => w.objectId === id)
}
export const selectWarehouse = (state: RootState, id: string) => {
  return state.warehouses.warehouses.find(w => w.objectId === id) || {} as warehouse.Warehouse
}
export const selectWarehouseAllStates = (state: RootState) => state.warehouses.state
export const selectWarehouseStateWarehouses = (state: RootState, id: string) => state.warehouses.state?.warehouses?.[id]
export const selectWarehouseStatePlace = (state: RootState, placeId: string) => state.warehouses.state?.places?.[placeId]

export default warehousesSlice.reducer
